import Vue from 'vue';
import i18n from '@/i18n';
import { formatAddress } from '@/utils/format';
import { ImageRun, Paragraph, Table, TableRow, TableCell, TextRun, WidthType, AlignmentType } from 'docx';
import { FONT, PAGE_WIDTH, NO_BORDER, THIN_BOTTOM_BORDER, THICK_BOTTOM_BORDER, cell, fixedTable, equalColumns, sectionHeading, fetchImageData, downloadDocx, DocxImageType } from '@/utils/docx-helpers';

export interface BeoExportData {
	order: Order;
	restaurant: Restaurant;
	reportSections: Record<string, boolean | Record<string, boolean>>;
	eventTimeline: { time: string, millis: number, value: string }[];
	groupedQuestions: { external: AnsweredCustomQuestion[], internal: AnsweredCustomQuestion[] };
	orderTaxInfo: { name: string, percentageValue: string, amount: number }[] | null;
	mergedItems: OrderMenuGroup[];
	formattedEventDate: string | null;
	imageURL: string;
}

/**
 * Build the event details section with restaurant info on the right.
 *
 * @param {BeoExportData} data
 * @param {{ data: Uint8Array, type: DocxImageType } | null} imageData
 * @return {(Paragraph | Table)[]}
 */
function buildEventDetails(data: BeoExportData, imageData: { data: Uint8Array, type: DocxImageType } | null): (Paragraph | Table)[] {
	const event = data.order.data.checkout.cateringEvent;
	const order = data.order.data;

	const restaurantName = data.restaurant.display_name || data.restaurant.name;

	const leftChildren: Paragraph[] = [];
	leftChildren.push(new Paragraph({
		children: [new TextRun({ text: event?.name || data.formattedEventDate || order.checkout.contact.full_name, bold: true, size: 32, font: FONT })]
	}));

	// Event header section
	if (event) {
		if (data.formattedEventDate) {
			leftChildren.push(new Paragraph({
				children: [new TextRun({ text: data.formattedEventDate, bold: true, size: 20, font: FONT })],
				spacing: { before: 120, after: 40 }
			}));
		}

		leftChildren.push(new Paragraph({
			children: [new TextRun({ text: i18n.tc('orders.beo.people', event.numPeople, { count: event.numPeople }), size: 20, font: FONT })],
			spacing: { before: 80 }
		}));

		const eventAddress = formatAddress(event.address, event.extra);
		if (eventAddress) {
			leftChildren.push(new Paragraph({
				children: [new TextRun({ text: eventAddress, bold: true, size: 20, font: FONT })],
				spacing: { before: 120, after: 40 }
			}));
		}
	}
	// Pickup/delivery header section
	else {
		const delivery = order.checkout.delivery;
		const addressText = delivery ? formatAddress(delivery.address, delivery.extra) : i18n.t('orders.details.pickup');

		if (addressText) {
			leftChildren.push(new Paragraph({
				children: [new TextRun({ text: addressText, bold: true, size: 20, font: FONT })],
				spacing: { before: 120, after: 40 }
			}));
		}
	}

	const rightChildren: Paragraph[] = [];
	rightChildren.push(new Paragraph({
		children: [new TextRun({ text: restaurantName, bold: true, size: 24, font: FONT })],
		alignment: AlignmentType.CENTER
	}));

	if (imageData) {
		rightChildren.push(new Paragraph({
			children: [new ImageRun({ data: imageData.data, transformation: { width: 120, height: 120 }, type: imageData.type })],
			alignment: AlignmentType.CENTER,
			spacing: { before: 120 }
		}));
	}

	const leftCol = Math.floor(PAGE_WIDTH * 0.6);
	const rightCol = PAGE_WIDTH - leftCol;

	return [fixedTable(
		[new TableRow({
			children: [
				new TableCell({ children: leftChildren, borders: NO_BORDER, width: { size: leftCol, type: WidthType.DXA } }),
				new TableCell({ children: rightChildren, borders: NO_BORDER, width: { size: rightCol, type: WidthType.DXA }, margins: { marginUnitType: WidthType.DXA, left: 200 } })
			]
		})],
		[leftCol, rightCol]
	)];
}

/**
 * Build the customer information section.
 *
 * @param {BeoExportData} data
 * @return {(Paragraph | Table)[]}
 */
function buildCustomerInfo(data: BeoExportData): (Paragraph | Table)[] {
	if (!data.reportSections.customerInformation) {
		return [];
	}

	const contact = data.order.data.checkout.contact;
	const values = [contact.full_name, contact.phone_number, contact.email].filter(Boolean);

	if (!values.length) {
		return [];
	}

	const cells = values.map(value => cell(value, NO_BORDER));

	return [
		...sectionHeading(i18n.t('orders.beo.customer_information')),
		fixedTable([new TableRow({ children: cells })], equalColumns(values.length))
	];
}

/**
 * Build the timeline section.
 *
 * @param {BeoExportData} data
 * @return {(Paragraph | Table)[]}
 */
function buildTimeline(data: BeoExportData): (Paragraph | Table)[] {
	if (!data.eventTimeline.length) {
		return [];
	}

	const timeCol = Math.floor(PAGE_WIDTH * 0.2);
	const valueCol = PAGE_WIDTH - timeCol;

	const rows = data.eventTimeline.map(timeEvent => new TableRow({
		children: [
			cell(timeEvent.time, THIN_BOTTOM_BORDER, { bold: true }),
			cell(timeEvent.value, THIN_BOTTOM_BORDER)
		]
	}));

	return [
		...sectionHeading(i18n.t('orders.beo.timeline')),
		fixedTable(rows, [timeCol, valueCol])
	];
}

/**
 * Build the special requirements section.
 *
 * @param {BeoExportData} data
 * @return {(Paragraph | Table)[]}
 */
function buildSpecialRequirements(data: BeoExportData): (Paragraph | Table)[] {
	if (!data.reportSections.specialRequirements || !data.order.data.checkout.pickup?.notes) {
		return [];
	}

	return [
		...sectionHeading(i18n.t('orders.beo.special_requirements')),
		fixedTable(
			[new TableRow({
				children: [cell(data.order.data.checkout.pickup.notes, NO_BORDER)]
			})],
			[PAGE_WIDTH]
		)
	];
}

/**
 * Build a custom fields section (external or internal).
 *
 * @param {AnsweredCustomQuestion[]} questions
 * @param {string} label
 * @return {(Paragraph | Table)[]}
 */
function buildCustomFields(questions: AnsweredCustomQuestion[], label: string): (Paragraph | Table)[] {
	if (!questions.length) {
		return [];
	}

	const rows = questions.map(field => new TableRow({
		children: [new TableCell({
			children: [new Paragraph({
				children: [
					new TextRun({ text: `${Vue.filter('localize')(field, 'question')}:  `, bold: true, size: 20, font: FONT }),
					new TextRun({ text: Vue.filter('localize')(field, 'answer'), size: 20, font: FONT })
				]
			})],
			borders: NO_BORDER,
			margins: { marginUnitType: WidthType.DXA, top: 20, bottom: 20 }
		})]
	}));

	return [
		...sectionHeading(label),
		fixedTable(rows, [PAGE_WIDTH])
	];
}

/**
 * Format item options as a string with bullet points.
 *
 * @param {OrderItem} item
 * @return {string[]}
 */
function formatItemOptions(item: OrderItem): string[] {
	const lines: string[] = [];
	item.orderOptions?.forEach((optionGroup: OrderOptionGroup) => {
		optionGroup.values?.forEach((option: OrderOption) => {
			if (option.quantity! > 0 && !option.default_selection) {
				let line = `• ${Vue.filter('localize')(option, 'name')}`;
				if (option.quantity! > 1) {
					line += ` (×${option.quantity})`;
				}
				lines.push(line);
			}
			else if (option.default_selection && option.quantity! > 1) {
				lines.push(`• ${Vue.filter('localize')(option, 'name')} (×${option.quantity})`);
			}
			else if (option.default_selection && option.quantity === 0) {
				lines.push(`• ${i18n.t('orders.beo.removed_option', { optionName: Vue.filter('localize')(option, 'name') })}`);
			}
		});
	});
	return lines;
}

/**
 * Format a number as currency.
 *
 * @param {number} value
 * @return {string}
 */
function formatCurrency(value: number): string {
	return i18n.n(value, 'currency');
}

/**
 * Build column widths for the order summary table.
 *
 * @param {boolean} showItemDetails
 * @param {boolean} showItemDescription
 * @param {boolean} showPrices
 * @return {number[]}
 */
function getOrderSummaryColumnWidths(showItemDetails: boolean, showItemDescription: boolean, showPrices: boolean): number[] {
	const menuWidth = 1400;
	const sectionWidth = 1400;
	let itemWidth = 1600;
	const qtyWidth = 800;
	const totalWidth = showPrices ? 1200 : 0;

	// Details and description share whatever space is left over, split evenly when both are shown
	const flexCount = (showItemDetails ? 1 : 0) + (showItemDescription ? 1 : 0);
	const remaining = PAGE_WIDTH - menuWidth - sectionWidth - itemWidth - qtyWidth - totalWidth;
	const flexWidth = flexCount ? Math.floor(remaining / flexCount) : 0;

	// With no flex column, give the leftover to the item name column so the table still fills the page
	if (!flexCount) {
		itemWidth += remaining;
	}

	const widths = [menuWidth, sectionWidth, itemWidth];
	if (showItemDetails) {
		widths.push(flexWidth);
	}
	if (showItemDescription) {
		widths.push(flexWidth);
	}
	widths.push(qtyWidth);
	if (showPrices) {
		widths.push(totalWidth);
	}

	return widths;
}

/**
 * Build a pricing footer row for the order summary table.
 *
 * @param {string} label
 * @param {string} amount
 * @param {number} colCount
 * @param {string} [percentage]
 * @param {boolean} [bold]
 * @return {TableRow}
 */
function pricingRow(label: string, amount: string, colCount: number, percentage?: string, bold?: boolean): TableRow {
	const spacerCells: TableCell[] = Array.from({ length: colCount - 3 }, () => cell('', NO_BORDER));

	return new TableRow({
		children: [
			...spacerCells,
			cell(label, NO_BORDER, { bold }),
			cell(percentage || '', NO_BORDER, { alignment: AlignmentType.RIGHT }),
			cell(amount, NO_BORDER, { bold, alignment: AlignmentType.RIGHT })
		]
	});
}

/**
 * Build the order summary table section.
 *
 * @param {BeoExportData} data
 * @return {(Paragraph | Table)[]}
 */
function buildOrderSummary(data: BeoExportData): (Paragraph | Table)[] {
	const showItemDetails = data.reportSections.itemDetails as boolean;
	const showItemDescription = data.reportSections.itemDescription as boolean;
	const showPrices = data.reportSections.prices as boolean;

	const headers: string[] = [
		i18n.t('orders.beo.table_header.menu'),
		i18n.t('orders.beo.table_header.section'),
		i18n.t('orders.beo.table_header.item_name')
	];

	if (showItemDetails) {
		headers.push(i18n.t('orders.beo.table_header.details'));
	}
	if (showItemDescription) {
		headers.push(i18n.t('orders.beo.table_header.description'));
	}
	headers.push(i18n.t('orders.beo.table_header.qty'));
	if (showPrices) {
		headers.push(i18n.t('orders.beo.table_header.total'));
	}

	const colCount = headers.length;
	const columnWidths = getOrderSummaryColumnWidths(showItemDetails, showItemDescription, showPrices);

	const headerRow = new TableRow({
		children: headers.map((header, index) => {
			const isRightAligned = index >= headers.length - (showPrices ? 2 : 1);
			return cell(header, THICK_BOTTOM_BORDER, { bold: true, alignment: isRightAligned ? AlignmentType.RIGHT : AlignmentType.LEFT });
		})
	});

	const allItems: { menu: OrderMenuGroup, section: OrderItemGroup, item: OrderItem }[] = [];
	const items = data.mergedItems.length ? data.mergedItems : data.order.data.items as unknown as OrderMenuGroup[];
	items.forEach((menu: OrderMenuGroup) => {
		menu.sections?.forEach((section: OrderItemGroup) => {
			section.items?.forEach((item: OrderItem) => {
				allItems.push({ menu, section, item });
			});
		});
	});

	const itemRows: TableRow[] = allItems.map((entry, index) => {
		const borders = index === allItems.length - 1 ? THICK_BOTTOM_BORDER : NO_BORDER;
		const cells: TableCell[] = [
			cell(Vue.filter('localize')(entry.menu, 'name'), borders),
			cell(Vue.filter('localize')(entry.section, 'name'), borders),
			cell(Vue.filter('localize')(entry.item, 'name'), borders)
		];

		if (showItemDetails) {
			const options = formatItemOptions(entry.item);
			const children: TextRun[] = options.map((line, i) => new TextRun({ text: line, size: 18, font: FONT, break: i > 0 ? 1 : undefined }));
			cells.push(new TableCell({
				children: [new Paragraph({ children })],
				borders,
				width: { size: 0, type: WidthType.AUTO },
				margins: { marginUnitType: WidthType.DXA, top: 40, bottom: 40 }
			}));
		}

		if (showItemDescription) {
			cells.push(cell(Vue.filter('localize')(entry.item, 'description') || '', borders));
		}

		cells.push(cell(`x${entry.item.quantity}`, borders, { alignment: AlignmentType.RIGHT }));

		if (showPrices) {
			cells.push(cell(formatCurrency(entry.item.orderPrice || 0), borders, { alignment: AlignmentType.RIGHT }));
		}

		return new TableRow({ children: cells });
	});

	const pricingRows: TableRow[] = [];
	if (showPrices) {
		const costs = data.order.data.costs;

		pricingRows.push(pricingRow(i18n.t('orders.report.orders_table.subtotal'), formatCurrency(costs.subtotal), colCount));

		if (costs.discount?.amount) {
			pricingRows.push(pricingRow(i18n.t('orders.report.orders_table.discount'), `-${formatCurrency(costs.discount.amount)}`, colCount));
		}

		if (costs.memberDiscount?.amount) {
			pricingRows.push(pricingRow(i18n.t('orders.report.orders_table.member_discount'), `-${formatCurrency(costs.memberDiscount.amount)}`, colCount));
		}

		if (costs.serviceCharge?.amount) {
			const pct = costs.serviceCharge.percentageValue ? `${costs.serviceCharge.percentageValue}%` : undefined;
			pricingRows.push(pricingRow(i18n.t('orders.report.orders_table.service_fee'), formatCurrency(costs.serviceCharge.amount), colCount, pct));
		}

		if (costs.deliveryCharge?.amount) {
			const pct = costs.deliveryCharge.percentageValue ? `${costs.deliveryCharge.percentageValue}%` : undefined;
			pricingRows.push(pricingRow(i18n.t('orders.report.orders_table.delivery_fee'), formatCurrency(costs.deliveryCharge.amount), colCount, pct));
		}

		if (data.restaurant.show_split_taxes && data.orderTaxInfo?.length) {
			data.orderTaxInfo.forEach(tax => {
				const pct = `${Math.round(Number(tax.percentageValue) * 100)}%`;
				pricingRows.push(pricingRow(tax.name, formatCurrency(tax.amount), colCount, pct));
			});
		}
		else {
			pricingRows.push(pricingRow(i18n.t('orders.report.orders_table.taxes'), formatCurrency(costs.taxes), colCount));
		}

		if (costs.tip?.amount) {
			pricingRows.push(pricingRow(i18n.t('orders.report.orders_table.amount'), formatCurrency(costs.total), colCount));
			const tipPct = costs.tip.percentageValue ? `${costs.tip.percentageValue}%` : undefined;
			pricingRows.push(pricingRow(i18n.t('orders.report.orders_table.gratuity'), formatCurrency(costs.tip.amount), colCount, tipPct));
		}

		pricingRows.push(pricingRow(i18n.t('orders.report.orders_table.total'), formatCurrency(costs.totalWithTip), colCount, undefined, true));
	}

	return [
		...sectionHeading(i18n.t('orders.beo.order_summary'), { pageBreak: true }),
		fixedTable([headerRow, ...itemRows, ...pricingRows], columnWidths)
	];
}

/**
 * Generate and download a DOCX file for the BEO report.
 *
 * @param {BeoExportData} data
 * @return {Promise<void>}
 */
export async function exportBeoToDocx(data: BeoExportData): Promise<void> {
	const imageData = data.imageURL ? await fetchImageData(data.imageURL) : null;

	const children: (Paragraph | Table)[] = [
		...buildEventDetails(data, imageData),
		...buildCustomerInfo(data),
		...buildTimeline(data),
		...buildSpecialRequirements(data)
	];

	if (data.reportSections.customFieldsExternal && data.groupedQuestions.external.length) {
		const label = `${i18n.t('orders.beo.custom_fields')} (${i18n.t('orders.beo.custom_fields_external')})`;
		children.push(...buildCustomFields(data.groupedQuestions.external, label));
	}

	if (data.reportSections.customFieldsInternal && data.groupedQuestions.internal.length) {
		const label = `${i18n.t('orders.beo.custom_fields')} (${i18n.t('orders.beo.custom_fields_internal')})`;
		children.push(...buildCustomFields(data.groupedQuestions.internal, label));
	}

	const orderSummary = buildOrderSummary(data);
	if (orderSummary.length) {
		children.push(...orderSummary);
	}

	const eventName = data.order.data.checkout.cateringEvent?.name || data.formattedEventDate || data.order.data.checkout.contact.full_name;
	const filename = `${i18n.t('orders.beo.print_title', { eventName })}.docx`;

	await downloadDocx(children, filename);
}
